import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, map, Observable, of } from 'rxjs';
import {
  IBaseOneResponse,
  IGetManyResponse,
  IGetOneResponse,
} from '../../../model/interface/crud-response-interface.model';
import { HelperService } from '../../helper.service';
import { Store } from '@ngrx/store';
import * as logbookAppReducer from '../../../../store/logbook.reducer';
import {
  IAddMyReport,
  IDeleteMyReport,
  IFormDropdownResponse,
  IAddEditDropdownDataResponse,
  ILogbookDropdownResponse,
  IMyReportsData,
  IReportLogsTableConfigurationDropdownData,
  ISelectedReportLogsTableConfigurationResponse,
  ISelectedReportData,
  IGetSelectedReportParams,
} from '../../../../store/reports/my-reports/my-reports.model';
import { IIssuer } from '../../../component/issuer/issuer.model';

@Injectable({
  providedIn: 'root',
})
export class MyReportsService {
  private readonly MY_REPORTS = {
    BASE_URL: '/my-reports/',
    BULK: 'bulk/',
    LOGBOOKS: '/logbooks/versions-form-assignments',
    FORMS: '/forms/versions-logbook-assignments',
    REPORT_LOGS_CONFIGURATION: '/configurations/report/',
  };

  constructor(
    public http: HttpClient,
    @Inject('API_BASE_URL') private readonly baseUrl: string,
    public readonly helperService: HelperService,
    public readonly store: Store<logbookAppReducer.LogbookAppState>,
  ) {}

  public getMyReportData(params: HttpParams): Observable<IGetManyResponse<IMyReportsData>> {
    return this.http.get<IGetManyResponse<IMyReportsData>>(`${this.baseUrl}${this.MY_REPORTS.BASE_URL}`, {
      params,
    });
  }

  public getReport(id: number): Observable<IGetOneResponse<IMyReportsData>> {
    return this.http.get<IGetOneResponse<IMyReportsData>>(`${this.baseUrl}${this.MY_REPORTS.BASE_URL}${id}/`);
  }

  public addReport(recordPayload: IAddMyReport, issuer: IIssuer | null): Observable<IBaseOneResponse<IAddMyReport>> {
    const url = `${this.baseUrl}${this.MY_REPORTS.BASE_URL}`;
    return this.http.post<IBaseOneResponse<IAddMyReport>>(url, {
      issuer,
      payload: recordPayload,
    });
  }

  public editReport(recordPayload: IAddMyReport, issuer: IIssuer | null): Observable<IBaseOneResponse<IAddMyReport>> {
    const url = `${this.baseUrl}${this.MY_REPORTS.BASE_URL}${recordPayload.id}/`;
    return this.http.put<IBaseOneResponse<IAddMyReport>>(url, {
      issuer,
      payload: recordPayload,
    });
  }

  public deleteReport(recordPayload: IAddMyReport, issuer: IIssuer | null): Observable<IBaseOneResponse<IAddMyReport>> {
    const url = `${this.baseUrl}${this.MY_REPORTS.BASE_URL}${recordPayload.id}/`;
    return this.http.delete<IBaseOneResponse<IAddMyReport>>(url, {
      body: {
        issuer,
        payload: recordPayload,
      },
    });
  }
  public bulkDeleteReport(
    recordPayload: IDeleteMyReport,
    issuer: IIssuer | null,
  ): Observable<IBaseOneResponse<IAddMyReport>> {
    const url = `${this.baseUrl}${this.MY_REPORTS.BASE_URL}${this.MY_REPORTS.BULK}`;
    return this.http.delete<IBaseOneResponse<IAddMyReport>>(url, {
      body: {
        issuer,
        payload: recordPayload,
      },
    });
  }

  public getLogbookDropdownData() {
    return this.http.get<IGetManyResponse<ILogbookDropdownResponse>>(`${this.baseUrl}${this.MY_REPORTS.LOGBOOKS}`);
  }

  public getFormDropdownData() {
    return this.http.get<IGetManyResponse<IFormDropdownResponse>>(`${this.baseUrl}${this.MY_REPORTS.FORMS}`);
  }

  public getReportLogsTableConfigurations(params: HttpParams) {
    return this.http.get<IGetManyResponse<IReportLogsTableConfigurationDropdownData>>(
      `${this.baseUrl}${this.MY_REPORTS.REPORT_LOGS_CONFIGURATION}`,
      { params },
    );
  }

  public getAddEditDropdownData(): Observable<IAddEditDropdownDataResponse> {
    const params: HttpParams = new HttpParams().set('getOnlyIdAndName', 'true');
    return forkJoin([
      this.getLogbookDropdownData(),
      this.getFormDropdownData(),
      this.getReportLogsTableConfigurations(params),
    ]).pipe(
      map(
        (
          responseList: [
            IGetManyResponse<ILogbookDropdownResponse>,
            IGetManyResponse<IFormDropdownResponse>,
            IGetManyResponse<IReportLogsTableConfigurationDropdownData>,
          ],
        ): IAddEditDropdownDataResponse => {
          return {
            logbookDropdownData: responseList?.[0]?.data ?? [],
            formDropdownData: responseList?.[1]?.data ?? [],
            reportLogsTableConfigurationDropdownData: responseList?.[2]?.data ?? [],
          };
        },
      ),
    );
  }

  public getSelectedReportLogsConfiguration(
    id: number,
  ): Observable<IBaseOneResponse<ISelectedReportLogsTableConfigurationResponse>> {
    return this.http.get<IBaseOneResponse<ISelectedReportLogsTableConfigurationResponse>>(
      `${this.baseUrl}${this.MY_REPORTS.REPORT_LOGS_CONFIGURATION}${id}`,
    );
  }

  public getSelectedReportData(params: IGetSelectedReportParams): Observable<ISelectedReportData> {
    const pageConfig$ = params.pageConfigurationId
      ? this.getSelectedReportLogsConfiguration(params.pageConfigurationId)
      : of<IBaseOneResponse<ISelectedReportLogsTableConfigurationResponse> | null>(null);

    return forkJoin({
      report: this.getReport(params.myFormEntryId),
      pageConfig: pageConfig$,
    }).pipe(
      map(
        ({ report, pageConfig }): ISelectedReportData => ({
          selectedData: report?.data ?? null,
          selectedPageConfigurationData: pageConfig?.data ?? null,
        }),
      ),
    );
  }
}
